import { mergeStyles } from '@fluentui/react';
import * as React from 'react';
import { AttachmentsContainer } from '../../containerComponents/AttachmentsContainer';
import { EmailFieldContainer } from '../../containerComponents/RequestDetails/EmailFieldContainer';
import { NotesFieldContainer } from '../../containerComponents/RequestDetails/NotesFieldContainer';
import { PhoneFieldContainer } from '../../containerComponents/RequestDetails/PhoneFieldContainer';
import { RequestPropertyContainer } from '../../containerComponents/RequestDetails/RequestPropertyContainer';
import { RequestStatusContainer } from '../../containerComponents/RequestDetails/RequestStatusContainer';
import { SeverityContainer } from '../../containerComponents/SeverityContainer';
import {
  FreelanceRequest,
  RequestPropertyKey,
  Note,
  RequestSeverity,
} from '../../models/requestModels';
import {
  getCommonClassNames,
  getCommonStyles,
} from '../../styles/commonStyles';
import {
  IRequestDetailsStyles,
  getRequestDetailsClassNames,
} from '../../styles/requestDetailsStyles';
import { IStyledComponent } from '../../styles/themesUI';
import { SectionHeader } from '../SectionHeader';
import { ErrorsField } from '../Shared/ErrorsField';
import { getString } from '../../../../../web/utils/stringLoader';

interface IRequestDetailsComponentProps {
  isAttachmentsEnabled: boolean;
  isRequestDetailsEditMode: boolean;
  pristineRequest: FreelanceRequest;
  request: FreelanceRequest;
  notes: Note[];
  showRequestSubNav: boolean;
  userEmail: string;
  errorList: string[];
  showError: boolean;
  isUnifiedPremier: boolean;
  isUnifiedEnterprisePremier: boolean;
  isNonUnifiedPremier: boolean;
  businessImpactConfirmed: boolean
  customerAvailabilityConfirmed: boolean;
  severity: RequestSeverity;
  isPremierTenantEnabled: boolean;
  // dispatchers
  setDirtyRequest: (request: FreelanceRequest) => void;
  resetRequestDetailsEditError: () => void;
}

type IRequestDetailsProps = IRequestDetailsComponentProps & IStyledComponent<IRequestDetailsStyles>;

export class RequestDetails extends React.Component<IRequestDetailsProps> {
  public componentDidUpdate = async (prevProps: IRequestDetailsProps) => {
    if (
      this.props.isRequestDetailsEditMode &&
      prevProps.isRequestDetailsEditMode !== this.props.isRequestDetailsEditMode
    ) {
      // update draft request for editing
      await this.props.setDirtyRequest(this.props.pristineRequest);
      this.props.resetRequestDetailsEditError();
    }
  };

  public render = (): React.ReactNode => {
    if (!this.props.request) {
      return null;
    }
    const commonClassNames = getCommonClassNames(getCommonStyles);
    const requestDetailsClassNames = getRequestDetailsClassNames(
      this.props.styles,
      { theme: this.props.theme, severity: this.props.severity }
    );
    const requestAttachmentsProps = {
      workspaceId: this.props.request?.WorkspaceId ? this.props.request.WorkspaceId : '',
      requestId: this.props.request?.RequestId,
      attachments: this.props.request?.Attachments,
      theme: this.props.theme,
      readOnlyMode: !this.props.isRequestDetailsEditMode,
    };
    const requestDetailsNotesProps = {
      notes: this.props.notes,
      isRequestDetailsEditMode: this.props.isRequestDetailsEditMode,
    };
    const requestDetailsEditErrorsProps = {
      errorList: this.props.errorList,
      showError: this.props.showError,
      theme: this.props.theme,
    };
    const requestDetailsSeverityProps = {
      isUnifiedPremier: this.props.isUnifiedPremier,
      isUnifiedEnterprisePremier: this.props.isUnifiedEnterprisePremier,
      isNonUnifiedPremier: this.props.isNonUnifiedPremier,
      businessImpactConfirmed: this.props.businessImpactConfirmed,
      customerAvailabilityConfirmed: this.props.customerAvailabilityConfirmed,
      severity: this.props.severity,
      theme: this.props.theme,
    };
    const isSeverityValid = !!this.props.severity && this.props.severity.toString() !== RequestSeverity.None.toString();
    return (
      <div className={requestDetailsClassNames.root}>
        <SectionHeader
          title={getString('requestDetails_mainTitle')}
          theme={this.props.theme}
          isUnifiedPremier={this.props.isUnifiedPremier}
          isUnifiedEnterprisePremier={this.props.isUnifiedEnterprisePremier}
          isNonUnifiedPremier={this.props.isNonUnifiedPremier}
          isPremierTenantEnabled={this.props.isPremierTenantEnabled}
        />
        <div className={requestDetailsClassNames.sectionContainer}>
          <div
            className={mergeStyles(
              commonClassNames.fontWeightBold,
              requestDetailsClassNames.contentContainer
            )}
          >
            {`${this.props.pristineRequest.Title} (#${this.props.pristineRequest.ParatureTicketNumber})`}
          </div>
          <div className={requestDetailsClassNames.propertyLine}>
            <div className={requestDetailsClassNames.propertyContentPrimary}>
              <RequestPropertyContainer
                requestPropertyKey={RequestPropertyKey.State}
                request={this.props.pristineRequest}
              />
              {
                isSeverityValid &&
                <span className={requestDetailsClassNames.propertyStateBullet}>
                  &bull;
                </span>
              }
            </div>
            <div className={requestDetailsClassNames.propertySeverityTitle}>
              {
                isSeverityValid &&
                <RequestPropertyContainer
                  requestPropertyKey={RequestPropertyKey.Severity}
                  request={this.props.request}
                />
              }
            </div>
          </div>
          <div className={requestDetailsClassNames.contentContainer}>
            {this.props.pristineRequest.UserDescription}
          </div>
        </div>
        {this.props.showRequestSubNav && (
          <div className={requestDetailsClassNames.sectionContainer}>
            <RequestStatusContainer request={this.props.pristineRequest} />
          </div>
        )}
        {this.props.isAttachmentsEnabled && <AttachmentsContainer {...requestAttachmentsProps} />}
        <NotesFieldContainer {...requestDetailsNotesProps} />
        <div className={requestDetailsClassNames.sectionContainer}>
          <div
            className={mergeStyles(
              commonClassNames.fontWeightBold,
              requestDetailsClassNames.contentContainer
            )}
          >
            {getString('requestDetails_serviceRequestDetailsTitle')}
          </div>
          <div className={requestDetailsClassNames.propertyLine}>
            <div className={requestDetailsClassNames.propertyLabel}>
              {getString('requestDetails_createdByLabel')}
            </div>
            <div>{this.props.pristineRequest.UserFirstName} {this.props.pristineRequest.UserLastName}</div>
          </div>
          {!!this.props.isPremierTenantEnabled && <SeverityContainer {...requestDetailsSeverityProps} />}
          <PhoneFieldContainer />
          <EmailFieldContainer />
          <ErrorsField {...requestDetailsEditErrorsProps} />
        </div>
      </div>
    );
  };
}
